import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLoaderData } from "react-router-dom";
import { useThemes } from "../utils/themeLoader";

import {
  fetchBuildingDataService,
  fetchQRCodeService,
  incrementSchedulesViewsPublicService,
} from "../Services/buildingService";

import useResponsiveLayout from "../Services/useResponsiveLayout";
import useTimeUpdate from "../Services/useTimeUpdate";

import PortraitScreen from "../components/common/PortraitScreen";
import LandscapeScreen from "../components/common/LandscapeScreen";
import LandscapeAdvertis from "../components/common/LandscapeAdvertis";

export const loader = async ({ params }) => {
  try {
    const data = await fetchBuildingDataService(params.id);
    return data;
  } catch (error) {
    console.error("Error loading building data:", error);
    return { error: "Failed to load building data" };
  }
};

const ShowBuildingScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const data = useLoaderData();

  // ניהול סטייטים
  const [building, setBuilding] = useState(data?.buildingData || null);
  const [messages, setMessages] = useState(data?.messages || []);
  const [news, setNews] = useState(data?.news || null);
  const [birthdays, setBirthdays] = useState(data?.birthdayMessages || []);
  const [weather, setWeather] = useState(data?.weather || null);
  const [campaign, setCampaign] = useState([]);
  const [smallCampaign, setSmallCampaign] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [template, setTemplate] = useState("default");
  const [address, setAddress] = useState("");
  const [didYouKnowItems, setDidYouKnowItems] = useState([]);
  const [media, setMedia] = useState(data?.media || []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { orientation, gridRows, getResponsiveMargin } = useResponsiveLayout();
  const scheduleViewsMap = useRef({});
  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const {
    currentTime,
    dayOfWeek,
    jewishDateInHebrewStr,
    isShabbat,
    updateTime,
  } = useTimeUpdate();

  const theme = useThemes(template);

  // ניקוי resources כאשר הקומפוננטה נהרסת
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // פונקציה לטיפול בשגיאות
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    setError(`שגיאה ב${context}: ${error.message || 'שגיאה לא ידועה'}`);
    
    // retry logic עם exponential backoff
    if (retryCountRef.current < maxRetries && isMountedRef.current) {
      retryCountRef.current += 1;
      const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
      
      setTimeout(() => {
        if (isMountedRef.current) {
          setError(null);
          if (context === 'fetchBuildingData') {
            fetchBuildingData();
          } else if (context === 'fetchQRCode') {
            fetchQRCode();
          }
        }
      }, retryDelay);
    }
  }, []);

  // פונקציות לשירותים עם error handling משופר
  const fetchQRCode = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      setIsLoading(true);
      const qrCodeUrl = await fetchQRCodeService();
      
      if (isMountedRef.current) {
        setQrCode(qrCodeUrl);
        setError(null);
        retryCountRef.current = 0;
      }
    } catch (error) {
      if (isMountedRef.current) {
        handleError(error, 'fetchQRCode');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [handleError]);

  const sendViewsToServer = useCallback(async (buildingId, scheduleCounts) => {
    try {
      await incrementSchedulesViewsPublicService(buildingId, scheduleCounts);
      console.log('Views sent successfully:', scheduleCounts);
    } catch (error) {
      console.error('Failed to send views:', error);
      // לא נעצור את הזרימה בגלל שגיאה בשליחת views
    }
  }, []);

  const fetchBuildingData = useCallback(async () => {
    if (!isMountedRef.current || !id) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetchBuildingDataService(id);
      
      if (!isMountedRef.current) return;
      
      const {
        buildingData,
        messages,
        weather,
        birthdayMessages,
        news,
        campaigns,
        smallCampaigns,
        didYouKnow,
        media,
      } = response;

      // עדכון state רק אם הקומפוננטה עדיין mounted
      setBuilding(buildingData);
      setMessages(messages || []);
      setWeather(weather);
      setBirthdays(birthdayMessages || []);
      setNews(news);
      setCampaign(campaigns || []);
      setSmallCampaign(smallCampaigns || []);
      setDidYouKnowItems(didYouKnow || []);
      setMedia(media || []);

      const fullAddress =
        buildingData?.nickname ||
        `${buildingData?.street} ${buildingData?.houseNumber}`;
      setAddress(fullAddress);
      setTemplate(buildingData?.designTemplate || "default");

      // שליחת views לשרת (אם יש)
      const scheduleCounts = scheduleViewsMap.current;
      const hasCounts = Object.keys(scheduleCounts).length > 0;

      if (hasCounts && buildingData?._id) {
        await sendViewsToServer(buildingData._id, scheduleCounts);
        scheduleViewsMap.current = {}; // איפוס אחרי שליחה מוצלחת
      }

      retryCountRef.current = 0; // איפוס counter אחרי הצלחה
      
    } catch (error) {
      if (isMountedRef.current) {
        handleError(error, 'fetchBuildingData');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [id, sendViewsToServer, handleError]);

  // בדיקת navigation עם cleanup
  useEffect(() => {
    if (!isMountedRef.current) return;

    const storedScreenId = localStorage.getItem("screenId");
    const storedSessionToken = localStorage.getItem("sessionToken");

    const currentPath = window.location.pathname + window.location.search;
    const targetPath = `/public/${storedScreenId}?sessionToken=${storedSessionToken}`;

    if (storedScreenId && storedSessionToken && currentPath !== targetPath) {
      navigate(targetPath);
    } else if (!storedScreenId || !storedSessionToken) {
      fetchQRCode();
    }
  }, [navigate, fetchQRCode]);

  // הגדרת intervals עם cleanup נכון
  useEffect(() => {
    if (!isMountedRef.current) return;

    // קריאה ראשונית
    fetchBuildingData();

    // הגדרת intervals
    const timeInterval = setInterval(() => {
      if (isMountedRef.current) {
        updateTime();
      }
    }, 30000);

    const dataInterval = setInterval(() => {
      if (isMountedRef.current) {
        fetchBuildingData();
      }
    }, 300000); // 5 דקות

    // cleanup
    return () => {
      clearInterval(timeInterval);
      clearInterval(dataInterval);
    };
  }, [fetchBuildingData, updateTime]);

  // רינדור מותנה עם טיפול בשגיאות
  if (error && !building) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>שגיאה בטעינת הנתונים</h2>
        <p>{error}</p>
        {retryCountRef.current < maxRetries && (
          <p>מנסה שוב... (ניסיון {retryCountRef.current + 1} מתוך {maxRetries})</p>
        )}
      </div>
    );
  }

  if (!building) {
    return qrCode ? (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h2>סרוק את הקוד כדי לחבר את המסך</h2>
        <img src={qrCode} alt="QR Code" style={{ maxWidth: '300px', maxHeight: '300px' }} />
        {isLoading && <p>טוען...</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </div>
    ) : (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  const videoUrl =
    "https://res.cloudinary.com/dlhfcdscf/video/upload/v1741595949/public/smallLogo_il9skv.mp4";

  // טיפול במצב שבת
  if (isShabbat && building.workOnSaturday?.includes("שחור")) {
    return (
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
        <img
          src="https://res.cloudinary.com/dlhfcdscf/image/upload/v1734344266/public/black_if30e2.jpg"
          alt="Shabbat"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  if (isShabbat && building.workOnSaturday === "תוצג תמונה קבועה ללא חדשות") {
    return (
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
        <img
          src="https://res.cloudinary.com/dlhfcdscf/image/upload/v1734345681/public/shabatShalom_zu6u8b.jpg"
          alt="Shabbat Banner"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  // רינדור לפי סוג בניין וכיוון מסך
  if (building.buildingType === "פרסום") {
    return (
      <LandscapeAdvertis
        theme={theme}
        currentTime={currentTime}
        dayOfWeek={dayOfWeek}
        weather={weather}
        address={address}
        campaigns={campaign}
        news={news}
        video={videoUrl}
        scheduleViewsMap={scheduleViewsMap}
      />
    );
  }

  // מזג מדיה של בניין לתוך רשימת הקמפיינים
  const mergedCampaign = useMemo(() => {
    if (!media?.length) return campaign;
    return [
      ...campaign,
      {
        scheduleId: '__building_media__',
        media: media.map((m) => ({ url: m.url, mediaType: m.mediaType })),
        durationInSeconds: 10,
      },
    ];
  }, [campaign, media]);

  const commonProps = {
    theme,
    currentTime,
    dayOfWeek,
    weather,
    address,
    smallCampaign,
    building,
    campaign: mergedCampaign,
    messages,
    birthdays,
    didYouKnowItems,
    news,
    video: videoUrl,
    scheduleViewsMap,
  };

  return orientation === "portrait" ? (
    <PortraitScreen
      {...commonProps}
      gridRows={gridRows}
      getResponsiveMargin={getResponsiveMargin}
    />
  ) : (
    <LandscapeScreen
      {...commonProps}
      gridRows={gridRows}
      getResponsiveMargin={getResponsiveMargin}
    />
  );
};

export default ShowBuildingScreen;